import React, { useState, useEffect, useCallback, useMemo } from 'react';
import uniqBy from 'lodash.uniqby';
import PropTypes from 'prop-types';
import Fuse from 'fuse.js';
import getCategories from '@core/spaceSelector/utils/getCategories';
import filterByCategory from '@core/spaceSelector/utils/filterByCategory';
import getSubCategories from '@core/spaceSelector/utils/getSubCategories';
import {
  Box,
  Separator,
  useTheme,
  Accordion,
  Area,
  Text,
} from '@ublend-npm/aulaui-core';
import useCategoryPreferences from '../hooks/useCategoryPreferences';
import SpaceList from './SpaceList';
import SpaceListAccordion from './SpaceListAccordion';
import StarSpaceOnboarding from './StarSpaceOnboarding';
import { StaffSpaces } from './StaffSpaces';

const Divider = () => <Separator sx={{ my: '0.5rem' }} />;
const starredCategory = {
  label: 'Starred spaces',
  order: -1,
};

// function to ensure space is valid
const isValidSpace = (space) =>
  space &&
  typeof space === 'object' &&
  'archived' in space &&
  Array.isArray(space.categories);

const SpaceSelector = ({
  spaces,
  onSpaceSelect,
  onSpaceStar,
  onSubCategorySelect,
  search = '',
  showArchived = false,
  sort = 'az',
  fetchRecentlyViewedSpaces,
}) => {
  // Remove local state for showArchived, filter, sortedBy
  const [fuse, setFuse] = useState(null);
  const [filteredSpaces, setFilteredSpaces] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [recentlyUnstarredSpaces, setRecentlyUnstarredSpaces] = useState([]);
  const [starredSpaces, setStarredSpaces] = useState([]);
  // To handle the opening and closing of the accordions
  const [openedAccordions, setOpenedAccordions] = useState(
    new Set([starredCategory.label])
  );
  const { palette } = useTheme();
  const categories = useMemo(
    () => [starredCategory, ...getCategories(spaces)],
    [spaces]
  );
  const { categoryPreferences, setSubCategoriesPreference } =
    useCategoryPreferences();

  let primaryCategory = null;

  if (categories.length > 0) {
    [primaryCategory] = categories;
  }

  useEffect(() => {
    if (categoryPreferences && categoryPreferences.category) {
      const category = categories.find(
        (c) => c.label === categoryPreferences.category.label
      );
      if (category) {
        setSelectedCategory(category);
      }
    } else {
      setSelectedCategory(primaryCategory);
    }
  }, [primaryCategory, categories, categoryPreferences]);

  useEffect(() => {
    setFuse(
      new Fuse(spaces, {
        threshold: 0.3,
        location: 0,
        distance: 1000,
        keys: ['name'],
        shouldSort: true,
      })
    );
  }, [spaces]);

  useEffect(() => {
    // Temp, To remove tabbar from the screen
    const tabbar = window.document.getElementById('space-settings-tabs');
    if (tabbar) {
      tabbar.style.display = 'none';
    }
    return () => {
      if (tabbar) {
        tabbar.style.display = 'block';
      }
    };
  }, []);

  // Use search prop for filtering
  const updateFiltered = useCallback(
    (query) => {
      if (!fuse || !query) {
        setFilteredSpaces(
          (showArchived
            ? spaces
            : spaces.filter((s) => isValidSpace(s) && !s.archived)
          ).filter(isValidSpace)
        );
        return;
      }
      const fuseResults = fuse
        .search(query)
        .map((r) => r.item)
        .filter(
          (item) => isValidSpace(item) && (showArchived || !item.archived)
        );
      const directMatches = spaces.filter(
        (space) =>
          isValidSpace(space) &&
          (showArchived || !space.archived) &&
          space.name?.toLowerCase().includes(query.toLowerCase())
      );
      const uniqueResults = uniqBy(
        [...directMatches, ...fuseResults],
        'id'
      ).filter(isValidSpace);
      setFilteredSpaces(uniqueResults);
    },
    [fuse, showArchived, spaces]
  );

  useEffect(() => {
    if (!search) {
      setFilteredSpaces(
        (showArchived ? spaces : spaces.filter((s) => !s.archived)).filter(
          isValidSpace
        )
      );
    } else {
      updateFiltered(search);
    }
  }, [search, showArchived, spaces, updateFiltered]);

  // Adding spaces to starred category as they are starred, and removing them when
  // they are unstarred only as the user navigates away from the starred category
  useEffect(() => {
    const newStarredSpaces = filteredSpaces.reduce((acc, s) => {
      const isRecentlyUnstarred = recentlyUnstarredSpaces.some(
        (rs) => rs.id === s.id
      );
      if (s.isStarred && !isRecentlyUnstarred) {
        return [...acc, { ...s, categories: [starredCategory] }];
      }
      return acc;
    }, []);
    setStarredSpaces(
      [...newStarredSpaces, ...recentlyUnstarredSpaces].filter(isValidSpace)
    );
  }, [filteredSpaces, recentlyUnstarredSpaces]);

  let shownSpaces = [...starredSpaces, ...filteredSpaces].filter(isValidSpace);
  if (!showArchived) {
    shownSpaces = shownSpaces.filter((space) => !space.archived);
  }

  // Apply sorting to the spaces
  const spacesToShow = shownSpaces.sort((a, b) => {
    if (sort === 'az') {
      return a.name.localeCompare(b.name);
    } else if (sort === 'za') {
      return b.name.localeCompare(a.name);
    }
    return 0;
  });

  const handleSpaceSelect = (id, subCategory = null) => {
    onSpaceSelect(
      id,
      showArchived,
      sort,
      selectedCategory ? selectedCategory.label : null,
      subCategory
    );
  };

  const handleSpaceStar = async (spaceId, subCategory = null) => {
    const space = starredSpaces.find((s) => s.id === spaceId);
    const hasBeenRecentlyUnstarred = recentlyUnstarredSpaces.some(
      (s) => s.id === spaceId
    );
    if (space && !hasBeenRecentlyUnstarred) {
      setRecentlyUnstarredSpaces((rus) => [...rus, space]);
    }
    await onSpaceStar({
      spaceId,
      showArchived,
      sort,
      search,
      category: selectedCategory?.label || null,
      subCategory,
    });
    await fetchRecentlyViewedSpaces();
  };

  // To handle the opening and closing of the accordions
  const handleOpenedAccordion = (uniqueCatagory) => {
    if (openedAccordions.has(uniqueCatagory)) {
      openedAccordions.delete(uniqueCatagory);
    } else {
      openedAccordions.add(uniqueCatagory);
    }
    setOpenedAccordions(new Set([...openedAccordions]));
  };

  const RenderStarredSpaces = useCallback(() => {
    if (!selectedCategory) {
      return null;
    }
    const spacesInStarredCategory = filterByCategory(
      spacesToShow,
      starredCategory
    );

    const allStaredArchived = spacesInStarredCategory?.every(
      (item) => item.archived === true
    );

    if (
      spacesInStarredCategory.length === 0 ||
      (allStaredArchived && !showArchived)
    ) {
      if (selectedCategory.label === starredCategory.label) {
        return <StarSpaceOnboarding />;
      }

      return (
        <Area>
          <Text>Your search didn’t match any of your spaces.</Text>
          <Text>Try with different keywords.</Text>
        </Area>
      );
    }

    return (
      <>
        <Accordion
          label={`Starred spaces (${spacesInStarredCategory.length})`}
          expanded={openedAccordions.has(starredCategory.label)}
          onChange={() => handleOpenedAccordion(starredCategory.label)}
          leftAligner
          detailsScrollAfter="20rem"
          size="large"
          sx={{
            bgcolor: 'transparent',
            '& .start-summary .MuiTypography-subtitle2': {
              fontSize: '20px',
              fontWeight: 600,
            },
          }}
        >
          <SpaceList
            spaces={[...spacesInStarredCategory].sort((a, b) => {
              if (sort === 'az') {
                return a.name.localeCompare(b.name);
              } else if (sort === 'za') {
                return b.name.localeCompare(a.name);
              }
              return 0;
            })}
            onClick={handleSpaceSelect}
            onStar={handleSpaceStar}
          />
        </Accordion>
        <Divider />
      </>
    );
  }, [starredSpaces?.length, filteredSpaces, sort]);

  const RenderPrimarySpacesAndSubSpaces = ({ category = null }) => {
    if (!selectedCategory) {
      return null;
    }
    const { label } = category;
    const subCategoriesOfCatagories = getSubCategories(spaces, label);
    const spacesAndSubSpacesAsPerCatagory = filterByCategory(
      spacesToShow,
      category
    );

    // Helper function to sort spaces
    const sortSpaces = (spacesToSort) => {
      return [...spacesToSort].sort((a, b) => {
        if (sort === 'az') {
          return a.name.localeCompare(b.name);
        } else if (sort === 'za') {
          return b.name.localeCompare(a.name);
        }
        return 0;
      });
    };

    // Sub Catagory Spaces With Accordion
    if (subCategoriesOfCatagories.length > 0) {
      return subCategoriesOfCatagories.map((subCategory) => {
        const subCategorySpaces = filterByCategory(
          spacesToShow,
          category,
          subCategory
        );
        const sortedSubCategorySpaces = sortSpaces(subCategorySpaces);

        return (
          <SpaceListAccordion
            key={subCategory.label}
            spaces={sortedSubCategorySpaces}
            label={subCategory.label}
            onClick={(id) => {
              handleSpaceSelect(id, subCategory.label);
            }}
            onStar={(id) => handleSpaceStar(id, subCategory.label)}
            expanded={openedAccordions.has(`${label}-${subCategory.label}`)}
            onChange={() => {
              handleOpenedAccordion(`${label}-${subCategory.label}`);
              if (openedAccordions.has(`${label}-${subCategory.label}`)) {
                onSubCategorySelect(label, subCategory.label);
              }
              setSubCategoriesPreference(subCategory);
            }}
          />
        );
      });
    }
    // Catagory Spaces With Accordion
    const sortedCategorySpaces = sortSpaces(spacesAndSubSpacesAsPerCatagory);
    return (
      <SpaceList
        spaces={sortedCategorySpaces}
        onClick={handleSpaceSelect}
        onStar={handleSpaceStar}
      />
    );
  };

  const RenderPrimarySpaces = useCallback(() => {
    return categories?.slice(1).map((category) => (
      <>
        <Accordion
          key={category.label}
          label={category.label}
          size="large"
          leftAligner
          detailsScrollAfter="20rem"
          expanded={openedAccordions.has(category.label)}
          onChange={() => handleOpenedAccordion(category.label)}
          sx={{
            bgcolor: 'transparent',
            '& .start-summary .MuiTypography-subtitle2': {
              fontSize: '20px',
              fontWeight: 600,
            },
          }}
        >
          <RenderPrimarySpacesAndSubSpaces category={category} />
        </Accordion>
        <Divider />
      </>
    ));
  }, [filteredSpaces?.length, sort]);

  return (
    <Box
      bgcolor={palette.background.default}
      display="flex"
      flexDirection="column"
      width="100%"
      height="calc(100% - 67px)"
      withHeaderDivider
      marginBottom={8}
    >
      <RenderStarredSpaces />
      <RenderPrimarySpaces />
      <StaffSpaces showArchived={showArchived} />
    </Box>
  );
};

SpaceSelector.propTypes = {
  spaces: PropTypes.arrayOf(PropTypes.shape({})),
  onSpaceSelect: PropTypes.func,
  onSpaceStar: PropTypes.func,
  onSubCategorySelect: PropTypes.func,
  search: PropTypes.string,
  showArchived: PropTypes.bool,
  sort: PropTypes.oneOf(['az', 'za']),
  fetchRecentlyViewedSpaces: PropTypes.func,
};

SpaceSelector.defaultProps = {
  spaces: [],
  onSpaceSelect: () => null,
  onSpaceStar: () => null,
  onSubCategorySelect: () => null,
  search: '',
  showArchived: false,
  sort: 'az',
  fetchRecentlyViewedSpaces: () => null,
};

export default React.memo(SpaceSelector);
